<template>
  <section :class="embedded ? 'mr-booking-embed' : 'dp-layout mr-page-layout'">
    <section :class="embedded ? '' : 'dp-right mr-module'">
      <div v-if="!embedded" class="page-header">
        <div>
          <div class="header-title-bar">
            <span class="header-icon-bar"></span>
            <h3>周期会议</h3>
          </div>
          <p class="page-desc">管理员代教师创建重复会议系列，或取消未开始的周期场次</p>
        </div>
        <el-tag v-if="schoolName" type="success" size="medium" effect="plain" class="school-badge">
          <i class="el-icon-school"></i> {{ schoolName }}
        </el-tag>
        <span v-else-if="!schoolId" class="school-hint">请在顶部选择学校</span>
      </div>

      <div v-if="!schoolId" class="empty-tip">请在顶部选择学校</div>

      <el-card v-else shadow="never" :class="['mr-panel', 'mr-booking-panel', { 'is-embedded': embedded }]">
        <div :class="['mr-booking-body', { 'is-embedded': embedded, 'is-tabs': embedded }]">
          <el-tabs v-model="activeTab" :class="['mr-booking-tabs', { 'is-embedded': embedded }]" @tab-click="onTabChange">
            <el-tab-pane label="新建周期会议" name="create">
              <div class="mr-booking-tab-scroll">
              <el-form ref="form" :model="form" :rules="formRules" label-width="110px" size="small"
                :class="{ 'mr-booking-form': embedded }" :style="embedded ? '' : 'max-width: 680px; margin-top: 12px;'">
                <el-form-item label="会议室" prop="RoomId">
                  <el-select v-model="form.RoomId" filterable placeholder="请选择会议室" style="width: 100%;"
                    :loading="roomLoading" @visible-change="loadRooms">
                    <el-option v-for="r in roomOptions" :key="r.Id" :label="r.Name" :value="r.Id" />
                  </el-select>
                </el-form-item>
                <el-form-item label="预约人" prop="BookerTeacherMasterId">
                  <MeetingRoomTeacherSelect v-model="form.BookerTeacherMasterId" :department-id="schoolId" />
                </el-form-item>
                <el-form-item label="会议主题" prop="Subject">
                  <el-input v-model="form.Subject" maxlength="200" placeholder="请输入会议主题" />
                </el-form-item>
                <el-form-item label="首次开始" prop="StartTime">
                  <el-date-picker v-model="form.StartTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss"
                    placeholder="首次开始时间" style="width: 100%;" :picker-options="bookingTimePickerOptions"
                    @change="onRecurringStartChange" />
                </el-form-item>
                <el-form-item label="首次结束" prop="EndTime">
                  <el-date-picker v-model="form.EndTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss"
                    placeholder="与开始同一天" style="width: 100%;" :picker-options="recurringEndPickerOptions"
                    :disabled="!form.StartTime" />
                  <p class="mr-booking-field-hint">周期按「同一天内的时段」重复（每日/每周/每月），不支持跨天会议</p>
                </el-form-item>
                <el-form-item label="重复类型" prop="RecurrenceType">
                  <el-radio-group v-model="form.RecurrenceType">
                    <el-radio :label="0">每日</el-radio>
                    <el-radio :label="1">每周</el-radio>
                    <el-radio :label="2">每月</el-radio>
                  </el-radio-group>
                </el-form-item>
                <el-form-item v-if="form.RecurrenceType === 1" label="重复星期">
                  <el-checkbox-group v-model="form.WeekDays">
                    <el-checkbox v-for="w in weekOptions" :key="w.value" :label="w.value">{{ w.label }}</el-checkbox>
                  </el-checkbox-group>
                </el-form-item>
                <el-form-item v-if="form.RecurrenceType === 2" label="每月第几天">
                  <el-input-number v-model="form.DayOfMonth" :min="0" :max="31" />
                  <span class="mr-booking-field-hint">0 表示与首次日期相同</span>
                </el-form-item>
                <el-form-item label="结束日期" prop="RecurrenceEndDate">
                  <el-date-picker v-model="form.RecurrenceEndDate" type="date" value-format="yyyy-MM-dd"
                    placeholder="重复结束日期" style="width: 100%;" />
                </el-form-item>
                <MeetingRoomParticipantPicker ref="participantPicker" v-model="participants"
                  :school-id="schoolId" :room-id="form.RoomId || 0"
                  :campus-id="bookingCampusId" :campus-name="bookingCampusName" />
                <MeetingRoomNotifyTypeField v-model="form.NotifyType" />
                <el-form-item label="备注">
                  <el-input v-model="form.Remark" type="textarea" :rows="2" placeholder="可选" />
                </el-form-item>
                <el-form-item v-if="!embedded">
                  <el-button type="primary" :loading="submitLoading" @click="submitForm">提交</el-button>
                  <el-button @click="resetForm">重置</el-button>
                </el-form-item>
              </el-form>
              <div v-if="lastResult" :class="['mr-booking-result', lastResult.success ? 'is-success' : 'is-error']">
                <i :class="lastResult.success ? 'el-icon-success' : 'el-icon-error'"></i>
                <span>{{ lastResult.message }}</span>
              </div>
              </div>
            </el-tab-pane>

            <el-tab-pane label="系列管理" name="manage">
              <div class="mr-booking-tab-scroll">
              <p class="mr-booking-manage-tip">查看并取消本校已创建的周期会议系列</p>
              <div class="toolbar-filter-bar">
              <el-form :inline="true" class="toolbar-filter-form mr-series-filter"
                @submit.native.prevent="searchSeries">
                <div class="toolbar-filter-fields">
                  <el-form-item label="主题">
                    <el-input v-model="seriesSubjectKeyword" clearable placeholder="会议主题"
                      class="toolbar-field-w-md" />
                  </el-form-item>
                  <el-form-item label="预约人">
                    <el-input v-model="seriesBookerKeyword" clearable placeholder="姓名"
                      class="toolbar-field-w-md" />
                  </el-form-item>
                  <el-form-item label="会议室">
                    <el-select v-model="seriesRoomFilter" clearable filterable placeholder="全部"
                      class="toolbar-field-w-md" :loading="roomLoading" @visible-change="loadRooms">
                      <el-option v-for="r in roomOptions" :key="r.Id" :label="r.Name" :value="r.Id" />
                    </el-select>
                  </el-form-item>
                  <el-form-item label="周期">
                    <el-select v-model="seriesRecurrenceTypeFilter" clearable placeholder="全部"
                      class="toolbar-field-w-md">
                      <el-option label="每日" :value="0" />
                      <el-option label="每周" :value="1" />
                      <el-option label="每月" :value="2" />
                    </el-select>
                  </el-form-item>
                  <el-form-item label="场次">
                    <el-select v-model="seriesUpcomingFilter" clearable placeholder="全部"
                      class="toolbar-field-w-md">
                      <el-option label="有未开始" value="upcoming" />
                      <el-option label="无未开始" value="ended" />
                    </el-select>
                  </el-form-item>
                  <el-form-item>
                    <el-button type="primary" size="small" icon="el-icon-search" native-type="submit">查询</el-button>
                  </el-form-item>
                </div>
                <div class="toolbar-filter-actions">
                  <el-button size="small" type="info" icon="el-icon-refresh-left" @click="resetSeriesFilters">重置</el-button>
                </div>
              </el-form>
              </div>
              <el-table :data="seriesList" v-loading="seriesLoading" border size="small"
                :class="['mr-table', 'mr-series-table', { 'is-embedded': embedded }]">
                <el-table-column label="会议" min-width="168" show-overflow-tooltip>
                  <template slot-scope="scope">
                    <div class="mr-series-cell">
                      <div class="mr-series-cell__title">{{ scope.row.Subject || '-' }}</div>
                      <div class="mr-series-cell__sub">{{ scope.row.BookerName || '-' }} · {{ scope.row.RoomName || '-' }}</div>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column label="周期" width="64" align="center">
                  <template slot-scope="scope">{{ recurrenceTypeText(scope.row.RecurrenceType) }}</template>
                </el-table-column>
                <el-table-column label="时间范围" min-width="148">
                  <template slot-scope="scope">
                    <div class="mr-series-cell">
                      <div class="mr-series-cell__line">{{ formatSeriesRange(scope.row) }}</div>
                      <div class="mr-series-cell__sub">重复至 {{ formatDate(scope.row.EndDate) }}</div>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column label="场次" width="72" align="center">
                  <template slot-scope="scope">
                    <div class="mr-series-cell mr-series-cell--count">
                      <span class="mr-series-cell__count">{{ scope.row.TotalCount || 0 }}</span>
                      <span class="mr-series-cell__sub">未开始 {{ scope.row.UpcomingCount || 0 }}</span>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="110" align="center">
                  <template slot-scope="scope">
                    <el-button type="text" size="mini" :disabled="!scope.row.UpcomingCount"
                      @click="cancelSeries(scope.row)">取消</el-button>
                  </template>
                </el-table-column>
              </el-table>
              <el-col :span="24" class="toolbar">
                <el-pagination :layout="embedded ? 'total, prev, pager, next' : 'total, prev, pager, next, jumper'"
                  :current-page="seriesPage" :page-size="seriesPageSize" :total="seriesTotal"
                  style="float: right" @current-change="handleSeriesPageChange" />
              </el-col>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>

        <div v-if="embedded" class="mr-booking-footer">
          <el-button @click="handleCancel">取消</el-button>
          <div v-if="activeTab === 'create'" class="mr-booking-footer__actions">
            <el-button @click="resetForm">重置</el-button>
            <el-button type="primary" :loading="submitLoading" @click="submitForm">确认创建</el-button>
          </div>
        </div>
      </el-card>
    </section>
  </section>
</template>

<script>
import axios from 'axios';
import { BaseApiUrl } from '../../api/base';
import { resolveActiveSchoolId } from '../../utils/deptCascaderProps';
import {
  meetingBookingTimePickerOptions,
  createMeetingStartTimeRule,
  createMeetingSameDayEndTimeRule,
  createSameDayEndPickerOptions,
  combineMeetingDateWithTimeOfDay
} from '../../utils/meetingRoomForm';

const apiBase = BaseApiUrl;

export default {
  name: 'MeetingRecurringBooking',
  props: {
    embedded: { type: Boolean, default: false }
  },
  components: {
    MeetingRoomTeacherSelect: () => import('../../components/MeetingRoomTeacherSelect.vue'),
    MeetingRoomParticipantPicker: () => import('../../components/MeetingRoomParticipantPicker.vue'),
    MeetingRoomNotifyTypeField: () => import('../../components/MeetingRoomNotifyTypeField.vue')
  },
  data() {
    return {
      activeTab: 'create',
      submitLoading: false,
      lastResult: null,
      seriesList: [],
      seriesLoading: false,
      seriesPage: 1,
      seriesPageSize: 10,
      seriesTotal: 0,
      seriesSubjectKeyword: '',
      seriesBookerKeyword: '',
      seriesRoomFilter: null,
      seriesRecurrenceTypeFilter: null,
      seriesUpcomingFilter: '',
      participants: [],
      roomOptions: [],
      roomLoading: false,
      roomLoadedForSchool: null,
      weekOptions: [
        { value: 1, label: '周一' }, { value: 2, label: '周二' }, { value: 3, label: '周三' },
        { value: 4, label: '周四' }, { value: 5, label: '周五' }, { value: 6, label: '周六' }, { value: 7, label: '周日' }
      ],
      form: {
        RoomId: null,
        BookerTeacherMasterId: null,
        Subject: '',
        StartTime: '',
        EndTime: '',
        RecurrenceType: 1,
        RecurrenceEndDate: '',
        WeekDays: [1, 2, 3, 4, 5],
        DayOfMonth: 0,
        Remark: '',
        NotifyType: 1
      },
      bookingTimePickerOptions: meetingBookingTimePickerOptions,
      formRules: {
        RoomId: [{ required: true, message: '请选择会议室', trigger: 'change' }],
        BookerTeacherMasterId: [{ required: true, message: '请选择预约人', trigger: 'change' }],
        Subject: [{ required: true, message: '请输入主题', trigger: 'blur' }],
        StartTime: [
          { required: true, message: '请选择开始时间', trigger: 'change' },
          createMeetingStartTimeRule()
        ],
        EndTime: [
          { required: true, message: '请选择结束时间', trigger: 'change' },
          createMeetingSameDayEndTimeRule(() => this.form.StartTime)
        ],
        RecurrenceEndDate: [{ required: true, message: '请选择结束日期', trigger: 'change' }]
      }
    };
  },
  computed: {
    schoolId() {
      return resolveActiveSchoolId(this);
    },
    schoolName() {
      return this.$store.state.activeSchoolName || '';
    },
    selectedRoom() {
      return this.roomOptions.find(r => r.Id === this.form.RoomId) || null;
    },
    bookingCampusId() {
      return this.selectedRoom ? Number(this.selectedRoom.CampusId) || 0 : 0;
    },
    bookingCampusName() {
      return (this.selectedRoom && this.selectedRoom.CampusName) || '';
    },
    recurringEndPickerOptions() {
      return createSameDayEndPickerOptions(() => this.form.StartTime);
    }
  },
  watch: {
    schoolId(val, oldVal) {
      if (val === oldVal) return;
      this.roomOptions = [];
      this.roomLoadedForSchool = null;
      this.participants = [];
      this.seriesList = [];
      this.seriesTotal = 0;
      this.resetSeriesFilters(false);
      if (val && this.activeTab === 'manage') this.loadSeries();
    }
  },
  methods: {
    /** 开始时间变更后，把结束时间对齐到同一天（保留结束时分）。最后更新：2026-07-19 */
    onRecurringStartChange() {
      if (!this.form.StartTime) {
        this.form.EndTime = '';
        return;
      }
      if (this.form.EndTime) {
        this.form.EndTime = combineMeetingDateWithTimeOfDay(this.form.StartTime, this.form.EndTime);
      }
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.validateField('EndTime');
      });
    },
    loadRooms(visible) {
      if (!visible || !this.schoolId || this.roomLoading) return;
      if (this.roomLoadedForSchool === this.schoolId && this.roomOptions.length) return;
      this.roomLoading = true;
      axios.get(`${apiBase}/api/MeetingRoom/GetList`, {
        params: { page: 1, intPageSize: 200, departmentId: this.schoolId }
      }).then(res => {
        this.roomLoading = false;
        this.roomLoadedForSchool = this.schoolId;
        this.roomOptions = (res.data.success && res.data.response && res.data.response.data) || [];
      }).catch(() => { this.roomLoading = false; });
    },
    buildParticipants() {
      const picker = this.$refs.participantPicker;
      return picker ? picker.buildPayload() : [];
    },
    onTabChange(tab) {
      if (tab.name === 'manage' && this.schoolId) this.loadSeries();
    },
    handleSeriesPageChange(val) {
      this.seriesPage = val;
      this.loadSeries();
    },
    loadSeries() {
      if (!this.schoolId) return;
      this.seriesLoading = true;
      const params = {
        schoolId: this.schoolId,
        page: this.seriesPage,
        pageSize: this.seriesPageSize
      };
      const subject = (this.seriesSubjectKeyword || '').trim();
      const booker = (this.seriesBookerKeyword || '').trim();
      if (subject) params.subjectKeyword = subject;
      if (booker) params.bookerKeyword = booker;
      if (this.seriesRoomFilter) params.roomId = this.seriesRoomFilter;
      if (this.seriesRecurrenceTypeFilter !== null && this.seriesRecurrenceTypeFilter !== '') {
        params.recurrenceType = this.seriesRecurrenceTypeFilter;
      }
      if (this.seriesUpcomingFilter) params.upcomingFilter = this.seriesUpcomingFilter;
      axios.get(`${apiBase}/api/MeetingBooking/GetRecurringSeriesList`, { params })
        .then(res => {
          this.seriesLoading = false;
          if (res.data.success) {
            this.seriesList = res.data.response.data || [];
            this.seriesTotal = res.data.response.dataCount || 0;
          }
        }).catch(() => { this.seriesLoading = false; });
    },
    searchSeries() {
      this.seriesPage = 1;
      this.loadSeries();
    },
    resetSeriesFilters(reload) {
      this.seriesSubjectKeyword = '';
      this.seriesBookerKeyword = '';
      this.seriesRoomFilter = null;
      this.seriesRecurrenceTypeFilter = null;
      this.seriesUpcomingFilter = '';
      this.seriesPage = 1;
      if (reload !== false && this.activeTab === 'manage' && this.schoolId) this.loadSeries();
    },
    submitForm() {
      this.$refs.form.validate(valid => {
        if (!valid) return;
        this.submitLoading = true;
        this.lastResult = null;
        axios.post(`${apiBase}/api/MeetingBooking/CreateRecurring`, {
          ...this.form,
          Participants: this.buildParticipants()
        }).then(res => {
          this.submitLoading = false;
          const data = res.data.response || {};
          this.lastResult = {
            success: res.data.success,
            message: res.data.msg || (res.data.success ? `成功创建 ${data.CreatedCount || 0} 条` : '提交失败')
          };
          if (res.data.success) {
            this.$message.success(this.lastResult.message);
            this.activeTab = 'manage';
            this.seriesPage = 1;
            this.loadSeries();
            this.$emit('success');
          } else {
            this.$message.error(this.lastResult.message);
          }
        }).catch(() => { this.submitLoading = false; });
      });
    },
    resetForm() {
      this.participants = [];
      if (this.$refs.participantPicker) this.$refs.participantPicker.reset();
      if (this.$refs.form) this.$refs.form.resetFields();
      this.lastResult = null;
    },
    cancelSeries(row) {
      this.$confirm(`确认取消周期系列「${row.Subject}」的全部未开始会议？`, '提示', { type: 'warning' }).then(() => {
        axios.post(`${apiBase}/api/MeetingBooking/CancelRecurring`, null, {
          params: { recurringId: row.RecurringId }
        }).then(res => {
          if (res.data.success) {
            this.$message.success(res.data.msg || '取消成功');
            this.loadSeries();
            this.$emit('success');
          } else {
            this.$message.error(res.data.msg || '取消失败');
          }
        });
      }).catch(() => {});
    },
    recurrenceTypeText(type) {
      const map = { 0: '每日', 1: '每周', 2: '每月' };
      return map[type] || type;
    },
    formatDateTime(val) {
      if (!val) return '-';
      return String(val).replace('T', ' ').slice(0, 16);
    },
    formatShortDateTime(val) {
      if (!val) return '-';
      const text = String(val).replace('T', ' ');
      const matched = text.match(/\d{4}-(\d{2}-\d{2}) (\d{2}:\d{2})/);
      if (matched) return `${matched[1]} ${matched[2]}`;
      return text.slice(0, 16);
    },
    formatSeriesRange(row) {
      const first = this.formatShortDateTime(row.FirstStartTime);
      const last = this.formatShortDateTime(row.LastStartTime);
      if (first === '-' && last === '-') return '-';
      if (first === last) return first;
      return `${first} ~ ${last}`;
    },
    formatDate(val) {
      if (!val) return '-';
      return String(val).slice(0, 10);
    },
    handleCancel() {
      this.$emit('cancel');
    }
  }
};
</script>

<style scoped>
.mr-booking-field-hint {
  margin-left: 8px;
  color: #94a3b8;
  font-size: 12px;
}

.mr-series-table >>> .el-table__body-wrapper {
  overflow-x: hidden;
}

.mr-series-table >>> table {
  width: 100% !important;
  table-layout: fixed;
}

.mr-series-cell__title {
  font-weight: 500;
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.4;
}

.mr-series-cell__line {
  font-size: 13px;
  color: #475569;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mr-series-cell__sub {
  margin-top: 2px;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mr-series-cell--count {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.mr-series-cell__count {
  font-size: 15px;
  font-weight: 600;
  color: #1e3a8a;
  line-height: 1.2;
}

.mr-series-table.is-embedded >>> td.el-table__cell {
  padding-top: 10px;
  padding-bottom: 10px;
}

.mr-series-table.is-embedded >>> .toolbar {
  margin-top: 12px;
}

.mr-series-filter {
  margin-bottom: 12px;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fafbfc;
}
</style>
